import React from 'react';
import { fixtures } from '../../data/fixtures';
import { standings } from '../../data/standings';
import { HOME_TEAM, TEAM_LOGOS } from '../../data/teams';
import { matchResult } from '../../pages/FixtureResult/matchUtils';

// Bronze-medal match and the biggest league win of the season.
const FEATURED_MATCH_NUMBERS = [32, 20];
const STANDINGS_PREVIEW_ROWS = 5;
const PREVIEW_COLUMNS = [
  { key: 'p', label: 'P' },
  { key: 'w', label: 'W' },
  { key: 'ws', label: 'WS' },
  { key: 'l', label: 'L' },
  { key: 'ls', label: 'LS' },
  { key: 'pts', label: 'PTS' },
];
const homeHighlight = 'rounded-full p-1 ring-2 ring-toofan-purple';

const TeamLogo = ({ team, className }) => (
  <img
    src={TEAM_LOGOS[team]}
    alt={`${team} logo`}
    className={`${className} ${team === HOME_TEAM ? homeHighlight : ''}`}
    loading="lazy"
  />
);

const FeaturedResult = ({ match }) => {
  const { homeGoals, awayGoals, winner, loser, shootout } = matchResult(match);
  const winnerGoals = Math.max(homeGoals, awayGoals);
  const loserGoals = Math.min(homeGoals, awayGoals);
  return (
    <div className="mb-4 py-1 text-center">
      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-gray-500">{match.stage}</p>
      <div className="flex items-center justify-evenly gap-4 py-2">
        <TeamLogo team={match.home} className="h-auto w-20" />
        <span className="text-2xl font-bold text-toofan-purple">
          {homeGoals} - {awayGoals}
        </span>
        <TeamLogo team={match.away} className="h-auto w-20" />
      </div>
      <h3 className="rounded-md bg-toofan-purple p-2 text-white">
        {winner} <span className="font-bold">beat</span> {loser} {winnerGoals} - {loserGoals}
        {shootout &&
          ` (shoot-out ${Math.max(shootout.home, shootout.away)} - ${Math.min(shootout.home, shootout.away)})`}
      </h3>
    </div>
  );
};

const StandingsPreview = () => (
  <div className="overflow-x-auto">
    <table className="min-w-full rounded-lg bg-white shadow-md">
      <thead className="bg-toofan-yellow text-center text-black">
        <tr>
          <th scope="col" className="border-b border-gray-200 px-3 py-2">
            Team
          </th>
          {PREVIEW_COLUMNS.map(({ key, label }) => (
            <th key={key} scope="col" className="border-b border-gray-200 px-2 py-2">
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {standings.slice(0, STANDINGS_PREVIEW_ROWS).map((row) => {
          const isHome = row.team === HOME_TEAM;
          return (
            <tr
              key={row.team}
              className={`even:bg-gray-50 ${isHome ? 'bg-yellow-50 ring-2 ring-inset ring-toofan-purple' : ''}`}
            >
              <td className="flex items-center justify-start gap-2 px-3 py-2 text-gray-800">
                <TeamLogo team={row.team} className="h-10 w-10 shrink-0 object-contain" />
                <span className={`text-sm font-semibold ${isHome ? 'text-toofan-purple' : ''}`}>{row.team}</span>
              </td>
              {PREVIEW_COLUMNS.map(({ key }) => (
                <td key={key} className="px-2 py-2 text-center font-semibold">
                  {row[key]}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
    <p className="mt-2 text-xs text-gray-500">WS / LS: won / lost in a shoot-out</p>
  </div>
);

const PointTable = () => {
  const featured = FEATURED_MATCH_NUMBERS.map((no) => fixtures.find((match) => match.matchNo === no)).filter(Boolean);

  return (
    <section className="m-2 mx-auto mt-10 items-center justify-center bg-gray-100 py-6 md:px-[9rem]">
      <div className="grid justify-center gap-8 md:flex">
        <div className="w-full max-w-lg rounded-lg p-4">
          <h2 className="my-2 mb-6 text-center text-3xl font-bold text-toofan-purple">Fixtures &amp; Results</h2>
          {featured.map((match) => (
            <FeaturedResult key={match.matchNo} match={match} />
          ))}
        </div>

        <div className="w-full max-w-lg rounded-lg bg-white p-4 shadow-md">
          <h2 className="my-2 mb-6 text-center text-3xl font-bold text-toofan-purple">Standings</h2>
          <StandingsPreview />
        </div>
      </div>
    </section>
  );
};

export default PointTable;
